import { Injectable, Injector, NgZone } from '@angular/core';
import { BaseViewModel } from '../../../../models/base/base-view-model';
import { AssetGroup } from '../../../../models/product/dto/asset-group';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { catchError, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { BsError } from '../../../../models/shared/bs-error';
import { Router } from '@angular/router';
import { Breadcrumb } from '../../../../models/shared/stylesheet/breadcrumb';
import { Card } from '../../../../models/shared/stylesheet/card';
import { ConfirmationOptions } from '../../../../models/shared/stylesheet/confirmation-options';
import { exists } from '../../../../functions/exists';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalCreateBrand } from '../../../../modals/modal-create-brand';
import { ModalAddBrandLogo } from '../../../../modals/modal-add-brand-logo';
import { ModalAddProductGroupsToBrand } from '../../../../modals/modal-add-product-groups-to-brand';
import { AssetGroupUtils } from '../../../../utils/asset-group-utils';
import { ModalUpdateAssetLibraryTypes } from '../../../../modals/modal-update-asset-library-types';
import { ModalAssetPreview } from '../../../../modals/modal-asset-preview';
import { ModalAddAsset } from '../../../../modals/modal-add-asset';
import { ModalGenericConfirmation } from '../../../../modals/modal-generic-confirmation';
import { ToastService } from '../../../../modules/toasts/service/toast.service';
import { ProductGroupDomainModel } from '../../../../domainModels/product-group-domain-model';
import { BrandDomainModel } from '../../../../domainModels/brand-domain-model';
import { TypeService } from '../../../../services/type-service';
import { Brand } from '../../../../models/product/dto/brand';
import { BrandFilterIndex, BrandFilterState } from './brands-filter-index';
import { LoadingOptions } from '../../../../models/shared/loading-options';
import { BrandsFiltersStateService } from './brands-filters/brands-filters-state.service';

// Provided by Logged In Scope
@Injectable()
export class BrandsViewModel extends BaseViewModel {
  constructor(
    private productGroupDomainModel: ProductGroupDomainModel,
    private brandDomainModel: BrandDomainModel,
    private ngZone: NgZone,
    private ngbModal: NgbModal,
    private injector: Injector,
    private router: Router,
    private toastService: ToastService,
    private typeService: TypeService,
    private filtersStateService: BrandsFiltersStateService
  ) {
    super();
    productGroupDomainModel.fetchNonHydratedBrandAssetGroupsIfEmpty();
    this.brandDomainModel.fetchBrandsIfEmpty();
    this.listenForBrandsLoading();
    // Seed the table with the persisted filters so it doesn't render unfiltered before the filters component binds.
    this.connectToFilterState(this.filtersStateService.toFilterState());
  }

  protected override _loadingOpts = new BehaviorSubject<LoadingOptions>(LoadingOptions.defaultWhiteBackground());

  public readonly brands$ = this.brandDomainModel.brands$;
  public readonly brandMap$ = this.brandDomainModel.brandMap$;

  private readonly _numberOfBrandsPerPage = new BehaviorSubject<number>(10);
  public readonly numberOfBrandsPerPage$ = this._numberOfBrandsPerPage as Observable<number>;
  public readonly connectToNumberOfBrandsPerPage = (value: number): void => this._numberOfBrandsPerPage.next(value);

  private readonly _resetFiltersRequest = new Subject<void>();
  public readonly resetFiltersRequest$ = this._resetFiltersRequest as Observable<void>;
  public readonly requestResetFilters = (): void => this._resetFiltersRequest.next();

  private readonly filterIndex = new BrandFilterIndex();

  private readonly _filterState = new BehaviorSubject<BrandFilterState>({
    selectedFilters: new Map(),
    visibilityControls: new Set(),
    searchText: ''
  });
  public readonly filterState$ = this._filterState as Observable<BrandFilterState>;
  public readonly connectToFilterState = (filterState: BrandFilterState): void => this._filterState.next(filterState);

  private readonly indexedBrands$ = this.brands$.pipe(
    tap(brands => {
      if (brands === null) {
        this.filterIndex.clear();
        return;
      }

      this.filterIndex.reconcile(brands);
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly filteredBrands$ = combineLatest([this.indexedBrands$, this.filterState$]).pipe(
    map(([brands, filterState]) => {
      if (brands === null) return null;
      return this.filterIndex.apply(filterState);
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  public readonly childBrandsByParentBrandId$: Observable<Map<string, Brand[]>> = this.filteredBrands$.pipe(
    map(brands => this.getChildBrandsByParentBrandId(brands)),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  // Built before the filters apply, so a search that hides sub-brand rows does not change a parent's sub-brand count.
  public readonly unfilteredChildBrandsByParentBrandId$: Observable<Map<string, Brand[]>> = this.indexedBrands$.pipe(
    map(brands => this.getChildBrandsByParentBrandId(brands)),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  public readonly brandsForTable$: Observable<Brand[] | null> = combineLatest([
    this.filteredBrands$,
    this.indexedBrands$,
    this.typeService.headquartersRegionNameMap$
  ]).pipe(
    map(([filteredBrands, allBrands, headquartersRegionNameMap]) => {
      if (filteredBrands === null || allBrands === null) return null;

      allBrands.forEach(brand => {
        brand.regionDisplayName = headquartersRegionNameMap?.get(brand?.stateCode) || brand?.stateCode || '';
      });

      return this.getParentBrandsForTable(filteredBrands, allBrands);
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  public readonly brandAssetGroups$ = this.productGroupDomainModel.brandAssetGroups$;

  private readonly _manuallyExpandedParentBrandIds = new BehaviorSubject<ReadonlySet<string>>(new Set());
  public readonly manuallyExpandedParentBrandIds$ = this._manuallyExpandedParentBrandIds as Observable<
    ReadonlySet<string>
  >;
  public readonly connectToManuallyExpandedParentBrandIds = (brandIds: ReadonlySet<string>): void => {
    this._manuallyExpandedParentBrandIds.next(brandIds);
  };
  public readonly expandedParentBrandIds$: Observable<ReadonlySet<string>> = combineLatest([
    this.manuallyExpandedParentBrandIds$,
    this.filteredBrands$,
    this.filterState$
  ]).pipe(
    map(([manuallyExpandedParentBrandIds, filteredBrands, filterState]) => {
      const expandedParentBrandIds = new Set(manuallyExpandedParentBrandIds);
      this.getSearchMatchedParentBrandIds(filteredBrands, filterState).forEach(parentBrandId => {
        expandedParentBrandIds.add(parentBrandId);
      });
      return expandedParentBrandIds;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private pruneExpandedIdsOfRemovedBrands = this.brands$.notNull().subscribeWhileAlive({
    owner: this,
    next: brands => this.removeExpandedParentBrandIdsMissingFrom(brands)
  });

  public readonly fetchingHydratedBrandDAGIds$ = this.productGroupDomainModel.fetchingHydratedBrandDAGIds$;

  private _selectedBrandAssetGroup: BehaviorSubject<AssetGroup> = new BehaviorSubject<AssetGroup>(null);
  public selectedBrandAssetGroup$ = this._selectedBrandAssetGroup as Observable<AssetGroup>;
  public groupName$ = this.selectedBrandAssetGroup$.pipe(map(brand => brand?.groupName));
  public hasLinkedAssetGroups$ = this.selectedBrandAssetGroup$.pipe(
    map(brand => brand?.linkedAssetGroupIds?.length > 0)
  );
  public linkedAssetGroups$ = combineLatest([
    this.productGroupDomainModel.displayAttributeGroups$,
    this.selectedBrandAssetGroup$
  ]).pipe(map(([gdags, brand]) => gdags?.filter(gdag => brand?.linkedAssetGroupIds.contains(gdag?.id))));

  public breadcrumbs$ = this.selectedBrandAssetGroup$.pipe(
    map(brand => {
      const brandsBc = new Breadcrumb('Brands', 'products/brands');
      const editBrandsBc = new Breadcrumb('Edit Brands', `products/brands/edit/${brand?.id}`);
      editBrandsBc.active = true;
      return [brandsBc, editBrandsBc];
    })
  );

  public canUpdateBrand$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  handleUpdatedBrands(brands: AssetGroup[]) {
    this.productGroupDomainModel.updateBrands(brands);
    this._selectedBrandAssetGroup.next(brands?.firstOrNull());
  }

  createBrand() {
    // BrandDomainModel.createBrand$ upserts into brands$, so brandsForTable$ picks up the new row on its own.
    const onContinueSetup = (brand: Brand) => {
      this.productGroupDomainModel.addNewBrands([AssetGroupUtils.brandAsAssetGroup(brand)]);
      const addProductGroups = () => {
        ModalAddProductGroupsToBrand.open(this.ngZone, this.ngbModal, this.injector, brand, true);
      };
      ModalAddBrandLogo.open(
        this.ngZone,
        this.ngbModal,
        this.injector,
        { brandId: brand?.id, brandName: brand?.name },
        addProductGroups
      );
    };
    ModalCreateBrand.open(this.ngZone, this.ngbModal, this.injector, undefined, undefined, onContinueSetup);
  }

  getDisplayAttributeGroups() {
    this.productGroupDomainModel.fetchNonHydratedDisplayAttributeGroupsIfEmpty();
  }

  getHydratedDisplayAttributesForSelectedBrandGroup() {
    const lm = 'Loading Display Attribute And Asset Groups';
    this._loadingOpts.addRequest(lm);
    this.selectedBrandAssetGroup$
      .pipe(take(1))
      .pipe(
        switchMap(brand => {
          const displayIds = brand?.linkedAssetGroupIds?.join(',');
          if (displayIds?.length > 0) {
            return this.productGroupDomainModel.getHydratedDisplayAttributeGroups(displayIds);
          } else {
            return of([]);
          }
        })
      )
      .subscribe({
        next: () => this._loadingOpts.removeRequest(lm),
        error: (err: BsError) => {
          this.toastService.publishError(err);
          this._loadingOpts.removeRequest(lm);
        },
        // negative acknowledgements send a complete signal to observers, so listen to it and handle it
        complete: () => this._loadingOpts.removeRequest(lm)
      });
  }

  getAndSetSelectedBrandAssetGroup(id: string) {
    const lm = 'Loading Brand Asset Group';
    this._loadingOpts.addRequest(lm);
    this.productGroupDomainModel.getHydratedBrandAssetGroups$(id).subscribe({
      next: brands => {
        this._loadingOpts.removeRequest(lm);
        if (exists(brands) && brands?.length > 0) {
          this._selectedBrandAssetGroup.next(brands[0]);
          this.getHydratedDisplayAttributesForSelectedBrandGroup();
        } else {
          this.router.navigate(['/products/brands']).then();
        }
      },
      error: (err: BsError) => {
        this.toastService.publishError(err);
        this._loadingOpts.removeRequest(lm);
        this.router.navigate([`/products/brands`]).then();
      }
    });
  }

  getHydrateBrandAssetGroups(brandAssetGroupIds: string[]) {
    this.productGroupDomainModel
      .fetchHydratedBrandAssetGroupsIfNeeded$(brandAssetGroupIds)
      .pipe(
        catchError((err: BsError) => {
          this.toastService.publishError(err);
          return of(null);
        })
      )
      .subscribe();
  }

  connectToToggleExpandedParentBrand(brand: Brand): void {
    const brandId = brand?.id;
    if (!brandId) return;

    this.manuallyExpandedParentBrandIds$.once(currentIds => {
      const manuallyExpandedParentBrandIds = new Set(currentIds);
      if (manuallyExpandedParentBrandIds.has(brandId)) {
        manuallyExpandedParentBrandIds.delete(brandId);
      } else {
        manuallyExpandedParentBrandIds.add(brandId);
      }
      this.connectToManuallyExpandedParentBrandIds(manuallyExpandedParentBrandIds);
    });
  }

  private removeExpandedParentBrandIdsMissingFrom(brands: Brand[]): void {
    const liveBrandIds = new Set((brands ?? []).map(brand => brand?.id));
    this.manuallyExpandedParentBrandIds$.once(currentIds => {
      const retainedIds = new Set([...currentIds].filter(brandId => liveBrandIds.has(brandId)));
      if (retainedIds.size === currentIds.size) return;
      this.connectToManuallyExpandedParentBrandIds(retainedIds);
    });
  }

  goToEditBrandPage(brand: Brand): void {
    this.router.navigate(['products/brands/edit', brand?.id]).then();
  }

  openUpdateAssetLibraryTypes() {
    this.selectedBrandAssetGroup$.pipe(take(1)).subscribe(brand => {
      ModalUpdateAssetLibraryTypes.open(
        this.ngZone,
        this.ngbModal,
        this.injector,
        brand,
        this.handleUpdatedBrands.bind(this)
      );
    });
  }

  openAssetPreview(card: Card) {
    this.selectedBrandAssetGroup$.once(brandGroup => {
      ModalAssetPreview.open(
        this.ngZone,
        this.ngbModal,
        this.injector,
        brandGroup?.id,
        card?.label,
        card?.asset,
        true,
        card?.data?.isDisabled,
        true,
        this.handleUpdatedBrands.bind(this)
      );
    });
  }

  openAddAsset(assetGroup: AssetGroup) {
    ModalAddAsset.open(this.ngZone, this.ngbModal, this.injector, assetGroup, this.handleUpdatedBrands.bind(this));
  }

  updateBrand(brand: AssetGroup) {
    const brandCopy = window.injector.Deserialize.instanceOf(AssetGroup, brand);
    const lm = 'Updating Brand';
    this._loadingOpts.addRequest(lm);
    this.productGroupDomainModel.updateAssetGroup$(brandCopy).subscribe(
      brands => {
        this._loadingOpts.removeRequest(lm);
        this.toastService.publishSuccessMessage('Successfully Updated Brand', 'Success');
        this.handleUpdatedBrands(brands);
      },
      (err: BsError) => {
        this._loadingOpts.removeRequest(lm);
        this.toastService.publishError(err);
      }
    );
  }

  goToEditProductGroupPage(id: string) {
    this.router.navigate(['products/productgroups/edit', id]);
  }

  trackByBrandId(index: number, brand: Brand): string {
    return brand?.id ?? `${index}`;
  }

  private getChildBrandsByParentBrandId(brands: Brand[] | null): Map<string, Brand[]> {
    const childBrandsByParentBrandId = new Map<string, Brand[]>();
    brands?.forEach(brand => {
      if (!brand?.parentBrandId) return;

      const childBrands = childBrandsByParentBrandId.get(brand.parentBrandId) ?? [];
      childBrands.push(brand);
      childBrandsByParentBrandId.set(brand.parentBrandId, childBrands);
    });

    return childBrandsByParentBrandId;
  }

  private getParentBrandsForTable(filteredBrands: Brand[], allBrands: Brand[]): Brand[] {
    const filteredBrandIds = new Set(filteredBrands.map(brand => brand?.id).filter(Boolean));
    const parentBrandIdsForFilteredChildren = new Set(
      filteredBrands.map(brand => brand?.parentBrandId).filter(parentBrandId => exists(parentBrandId))
    );

    return allBrands.filter(brand => {
      const isFilteredTopLevelBrand = filteredBrandIds.has(brand?.id) && !brand?.parentBrandId;
      const hasFilteredChildren = parentBrandIdsForFilteredChildren.has(brand?.id);
      return isFilteredTopLevelBrand || hasFilteredChildren;
    });
  }

  private getSearchMatchedParentBrandIds(
    filteredBrands: Brand[] | null,
    filterState: BrandFilterState
  ): ReadonlySet<string> {
    if (!filterState?.searchText?.trim()) return new Set();

    return new Set(filteredBrands?.map(brand => brand?.parentBrandId).filter(parentBrandId => exists(parentBrandId)));
  }

  openDeleteBrandConfirmationModal(brand: AssetGroup) {
    const opts = new ConfirmationOptions();
    opts.title = `Delete ${brand.groupName}?`;
    opts.bodyText =
      'Are you sure you want to delete this Brand Asset Group? ' +
      'All associated assets will be deleted and any linked asset groups will become unlinked. These groups will no ' +
      'longer have brand assets associated with them.';
    opts.cancelText = 'Cancel';
    opts.continueText = 'Delete Brand';
    ModalGenericConfirmation.open(
      this.ngZone,
      this.ngbModal,
      this.injector,
      opts,
      cont => cont && this.deleteBrandAssetGroup(brand)
    );
  }

  protected deleteBrandAssetGroup(brand: AssetGroup) {
    const lm = 'Deleting Brand Asset Group';
    this._loadingOpts.addRequest(lm);
    const brandCopy = window.injector.Deserialize.instanceOf(AssetGroup, brand);
    this.productGroupDomainModel.deleteBrandAssetGroup$(brandCopy).subscribe({
      next: () => {
        this._loadingOpts.removeRequest(lm);
        this.toastService.publishSuccessMessage('Brand Asset Group Deleted', 'Success');
        this._selectedBrandAssetGroup.next(null);
        this.router.navigate(['products/brands']).then();
      },
      error: (err: BsError) => {
        this._loadingOpts.removeRequest(lm);
        this.toastService.publishError(err);
      }
    });
  }

  private listenForBrandsLoading(): void {
    this.brandsForTable$.subscribeWhileAlive({
      owner: this,
      next: brands => {
        const lm = 'Loading Brands';
        if (!brands) {
          this._loadingOpts.addRequest(lm);
        } else {
          this._loadingOpts.removeRequest(lm);
        }
      }
    });
  }
}
